import type * as yup from 'yup';
import type { AttributeType, DataSchema, FormItemDefinition, GetValueFormItems, RecordsViewParam, Schema, ThreeLevelsFilterValue } from 'types';
import type { JoinedRecordRef } from 'types/records';
import type { DISPLAY_NESTED_LIST_COLUMNS, INTERACTION_TYPE, TABLE_CELL_TYPE, USER_AGENDA_TABLE_PRESENTATION_MODE, VIEW_TYPE } from 'types/enums';
import type { calculateResponseSchema } from 'utils/validations/useCalculateResponseSchema';
import type { FieldError } from 'react-hook-form';

export type InlineRecordType = {
  agendaId: string;
  recordId: string;
  attributes: AttributeType[];
};

export type JoinedColumnLevel = 'nested' | 'secondLevel';

export type JoinedColumnSource = {
  level: JoinedColumnLevel;
  formItemId: string;
  agendaId: string | null;
};

export type JoinedRowIdentity = {
  parentRecordId?: string;
  nestedRecord: JoinedRecordRef | null;
  secondLevelNestedRecord: JoinedRecordRef | null;
};
export type RowsMap = Map<string, AttributeType[]>;
export type CalculateResponseSchemaType = yup.InferType<typeof calculateResponseSchema>;
export type CalculateResponseAttribute = CalculateResponseSchemaType['attributes'][number];

export interface UserAgendaTableDataSchema {
  formItemDefinition?: FormItemDefinition;
  label: string;
  type: TABLE_CELL_TYPE;
  originalType?: string;
  customKey: string;
  group: string;
  groupName: string;
  groupOrder: number;
  dateMask?: string;
  isEditable: boolean;
  joinedColumn?: JoinedColumnSource;
  showInToolbar?: boolean;
  order?: number;
  columnId?: string;
}

export type UserAgendaTableDataSchemaType = Record<string, UserAgendaTableDataSchema>;

export interface ValidationResult {
  allFilled: boolean;
  missingFields: string[];
}

export interface UserAgendaTableProps {
  error?: FieldError;
  agendaId: string;
  viewId: string;
  recordId?: string;
  parentAgendaId?: string;
  parentAgendaName?: string;
  parentAgendaModule?: { id: string; parent?: { id: string } };
  formItemId?: string;
  onValueChange?: (attributes?: InlineRecordType[]) => void;
  unableSortFor?: 'all' | 'none' | (string | number)[];
  onSelectRow?: (rowIds: string[], rows?: Schema<string | number, DataSchema>[]) => void;
  selectedRows?: string[];
  value?: string[];
  isOverview?: boolean;
  isModule?: boolean;
  isModal?: boolean;
  modalOverrideCalculateValues?: InlineRecordType[];
  presentationMode?: USER_AGENDA_TABLE_PRESENTATION_MODE;
  displayNestedListColumns?: DISPLAY_NESTED_LIST_COLUMNS;
  isDetail?: boolean;
  hidePagination?: boolean;
  hideCheckbox?: boolean;
  disableRowClick?: boolean;
  isMultiSelect?: boolean;
  rowsCount?: number;
  params?: RecordsViewParam[];
  overrideParams?: GetValueFormItems[];
  isFetching?: boolean;
  interactions?: INTERACTION_TYPE[];
  isEditableColumnsAllowed?: boolean;
  evaluateVisualAttributes?: boolean;
  isPinnedDashboard?: boolean;
  backgroundColor?: string;
  name?: string;
  onClick?: () => void;
  displayButtons?: boolean;
  formNamespace?: string;
  disableSaveButton?: boolean;
  viewType?: VIEW_TYPE;
  viewWidgetId?: string | null;
  showAllRows?: boolean;
  initialSearchTerm?: string;
  isAutoSelectSingleResult?: boolean;
  onAutoSelectSingleResult?: (rowId: string, row: Schema<string | number, DataSchema>) => void;
  filterRecordIds?: string[];
  onClearNotificationsFilter?: () => void;
  noData?: string;
  testIdPrefix?: string;
  disableColumnResize?: boolean;
  reserveHeaderControlsSpace?: boolean;
  hideToolbar?: boolean;
  disableRowActions?: boolean;
}

export interface UseMapFilterValuesProps {
  referencedFormItemsMap: Map<string, string>;
  use3LevelStructure?: boolean;
}

export interface MappedFilterValue {
  formItemId: string;
  formItemType: string;
  values?: string[];
  value?: string;
  fromValue?: string;
  toValue?: string;
  referencedFormItemId?: string;
}

export type MappedFilterResult = MappedFilterValue | ThreeLevelsFilterValue;

export const isMappedFilterValue = (value: MappedFilterResult): value is MappedFilterValue => {
  return 'formItemId' in value;
};

export const isThreeLevelsFilterValue = (value: MappedFilterResult): value is ThreeLevelsFilterValue => {
  return 'mainFormItemId' in value;
};
